import { Inject, Injectable } from '@angular/core';
import { map, Observable, of, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { AuxiliaryDataFieldSchemaInterface, AuxiliaryDataFieldType } from '@vendasta/account-group';

interface ExtraData {
  objectType: ObjectType;
  supportedFieldTypes: AuxiliaryDataFieldType[];
}

@Injectable({ providedIn: 'root' })
export class AuxiliaryDataSelectorService
  implements AutomationReplacementChipsDataService<AuxiliaryDataFieldSchemaInterface>
{
  supportsSearching = false;

  constructor(
    private readonly schemaService: SchemaService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {}

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData: ExtraData,
  ): Observable<PagedResponse<AuxiliaryDataFieldSchemaInterface>> {
    return this.namespace$.pipe(
      switchMap((namespace) =>
        this.schemaService.listFieldSchemas(extraData.objectType, namespace, false, cursor, pageSize),
      ),
      map((resp) => {
        resp.fieldSchemas = (resp?.fieldSchemas || []).filter((x) =>
          extraData.supportedFieldTypes.includes(x.fieldType),
        );
        return resp;
      }),
      take(1),
      map((resp) => {
        return {
          results: resp?.fieldSchemas,
          hasMore: resp?.pagingMetadata?.hasMore,
          nextCursor: resp?.pagingMetadata?.nextCursor,
        };
      }),
    );
  }

  convertDataToDisplay(s: AuxiliaryDataFieldSchemaInterface): Observable<string> {
    return of(s?.fieldName || '');
  }

  convertDataToFormValue(x: AuxiliaryDataFieldSchemaInterface): any {
    return x.fieldId;
  }

  getMulti(fieldIds: string[], extraData: ExtraData): Observable<AuxiliaryDataFieldSchemaInterface[]> {
    return this.namespace$.pipe(
      switchMap((namespace) =>
        this.schemaService.getMultiFieldSchema(
          extraData.objectType,
          fieldIds.map((fieldId) => ({
            partnerId: namespace,
            fieldId,
          })),
        ),
      ),
      map((resp) => resp?.fieldSchemas || []),
    );
  }
}
